import { computed, makeObservable, observable, action, runInAction } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { Common } from 'src/domains/common/Common';
import { LocalStorageState } from 'src/domains/layouts/state/localStorage/LocalStorageState';
import { streaming } from 'src_server/trpc/types';
import * as uuid from 'uuid';
import {
    GeniusPlayerUiStatusType,
    GeniusSportsMessageDetailType,
    logGeniusPlayerError,
    resolveGeniusDevice,
    resolveGeniusProtocol,
    resolveGeniusRegion,
} from './geniusLivePlayer.types';

export class GeniusLivePlayerState {
    private readonly metadataResource: Resource<streaming.GeniusMetadataResponseType>;
    private readonly endUserSessionId: string;
    private readonly deviceId: string;

    @observable public uiStatus: GeniusPlayerUiStatusType = 'loading';
    @observable public containerId: string;
    @observable public isPlaybackStarted: boolean = false;

    public constructor(
        private readonly common: Common,
        private readonly eventId: number
    ) {
        makeObservable(this);

        this.containerId = `geniusLivePlayer-${eventId}`;
        this.endUserSessionId = uuid.v4();
        this.deviceId = LocalStorageState.get(common).getOrCreateGeniusDeviceId();

        this.metadataResource = new Resource(async (): Promise<streaming.GeniusMetadataResponseType> => {
            return await this.common.trpcClient.client.streaming.getGeniusMetadata.query({
                eventId: this.eventId,
                deviceId: this.deviceId,
            });
        });
    }

    @computed public get metadataResult(): ReturnType<Resource<streaming.GeniusMetadataResponseType>['get']> {
        return this.metadataResource.get();
    }

    @computed public get metadata(): streaming.GeniusMetadataResponseType | null {
        const result = this.metadataResult;
        if (result.type !== 'ready') {
            return null;
        }

        return result.value;
    }

    @computed public get isAvailable(): boolean {
        const metadata = this.metadata;
        return metadata !== null && metadata.available === true && metadata.availability === 'AVAILABLE';
    }

    @computed private get hasPlayableConfig(): boolean {
        const metadata = this.metadata;
        if (metadata === null) {
            return false;
        }

        return (
            metadata.glpWidgetUrl !== undefined &&
            metadata.customerId !== undefined &&
            metadata.fixtureId !== null &&
            metadata.fixtureId !== undefined &&
            metadata.playerDigest !== undefined
        );
    }

    @computed public get canMountPlayer(): boolean {
        return (
            this.isAvailable &&
            this.hasPlayableConfig &&
            this.uiStatus !== 'playbackError' &&
            this.uiStatus !== 'unavailable'
        );
    }

    /** Only true after Genius delivery + player.start — safe to hide scoreboards. */
    @computed public get claimsLead(): boolean {
        return this.isPlaybackStarted;
    }

    @computed public get maxPlayerSizePercentage(): number {
        const permissions = this.metadata?.liveStreams?.[0]?.permissions;
        const percentage = permissions?.[0]?.maxPlayerSizePercentage;
        if (typeof percentage === 'number' && percentage > 0 && percentage <= 100) {
            return percentage;
        }

        return 100;
    }

    @action public syncUiStatusFromMetadata = (): void => {
        const result = this.metadataResult;

        if (result.type === 'loading') {
            this.uiStatus = 'loading';
            this.isPlaybackStarted = false;
            return;
        }

        if (result.type === 'error') {
            logGeniusPlayerError('metadata_error');
            this.uiStatus = 'unavailable';
            this.isPlaybackStarted = false;
            return;
        }

        if (result.value.available !== true || result.value.availability !== 'AVAILABLE') {
            if (result.value.availability !== 'DISABLED' && result.value.availability !== 'UNMATCHED') {
                logGeniusPlayerError('metadata_unavailable', result.value.availability);
            }
            this.uiStatus = 'unavailable';
            this.isPlaybackStarted = false;
            return;
        }

        if (
            result.value.glpWidgetUrl === undefined ||
            result.value.customerId === undefined ||
            result.value.fixtureId === null ||
            result.value.fixtureId === undefined ||
            result.value.playerDigest === undefined
        ) {
            logGeniusPlayerError('incomplete_player_config', result.value.availability);
            this.uiStatus = 'unavailable';
            this.isPlaybackStarted = false;
            return;
        }

        this.uiStatus = 'ready';
    };

    public getWidgetLoaderSrc(): string | null {
        const metadata = this.metadata;
        if (metadata === null || !this.isAvailable) {
            return null;
        }

        const { glpWidgetUrl, customerId, fixtureId, playerDigest } = metadata;
        if (
            glpWidgetUrl === undefined ||
            customerId === undefined ||
            fixtureId === null ||
            fixtureId === undefined ||
            playerDigest === undefined
        ) {
            logGeniusPlayerError('incomplete_player_config', metadata.availability);
            return null;
        }

        const params = new URLSearchParams({
            customerId,
            fixtureId,
            deviceId: this.deviceId,
            digest: playerDigest,
            containerId: this.containerId,
            width: '100%',
            height: '100%',
            autoplayEnabled: 'true',
            audioEnabled: 'false',
            controlsEnabled: 'true',
            allowFullScreen: 'true',
        });

        const separator = glpWidgetUrl.includes('?') ? '&' : '?';
        return `${glpWidgetUrl}${separator}${params.toString()}`;
    }

    public handleGeniusMessage = async (event: Event): Promise<void> => {
        const customEvent = event as CustomEvent<GeniusSportsMessageDetailType>;
        const detail = customEvent.detail;

        if (detail === undefined || detail === null) {
            return;
        }

        if (detail.type === 'player_not_ready') {
            logGeniusPlayerError('player_not_ready');
            runInAction(() => {
                this.uiStatus = 'playbackError';
                this.isPlaybackStarted = false;
            });
            return;
        }

        if (detail.type !== 'player_ready') {
            return;
        }

        const body = detail.body;
        const protocol = resolveGeniusProtocol(body.deliveryType);
        const streamId = body.streamId;
        const deliveryId = body.deliveryId;

        if (protocol === null || streamId === undefined || deliveryId === undefined) {
            logGeniusPlayerError('player_ready_invalid_payload');
            runInAction(() => {
                this.uiStatus = 'playbackError';
                this.isPlaybackStarted = false;
            });
            return;
        }

        try {
            const delivery = await this.common.trpcClient.client.streaming.getGeniusDelivery.mutate({
                eventId: this.eventId,
                streamId,
                protocol,
                deliveryId,
                endUserSessionId: this.endUserSessionId,
                region: resolveGeniusRegion(body.region),
                device: resolveGeniusDevice(body.device),
            });

            if (delivery.available !== true || delivery.url === undefined || delivery.token === undefined) {
                logGeniusPlayerError('delivery_unavailable', delivery.availability);
                runInAction(() => {
                    this.uiStatus = 'playbackError';
                    this.isPlaybackStarted = false;
                });
                return;
            }

            const player = window.GeniusLivePlayer?.player;
            if (player === undefined) {
                logGeniusPlayerError('player_api_missing');
                runInAction(() => {
                    this.uiStatus = 'playbackError';
                    this.isPlaybackStarted = false;
                });
                return;
            }

            player.start({
                url: delivery.url,
                token: delivery.token,
                expiresAt: delivery.expiresAt,
                drm: delivery.drm,
            });

            runInAction(() => {
                this.uiStatus = 'ready';
                this.isPlaybackStarted = true;
            });
        } catch (_error) {
            logGeniusPlayerError('delivery_request_failed');
            runInAction(() => {
                this.uiStatus = 'playbackError';
                this.isPlaybackStarted = false;
            });
        }
    };

    public closePlayer = (): void => {
        try {
            window.GeniusLivePlayer?.player.close();
        } catch (_error) {
            logGeniusPlayerError('player_close_failed');
        }

        runInAction(() => {
            this.isPlaybackStarted = false;
        });
    };
}
